import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { PageHeader, Loading, StatusBadge, Modal, Field, ErrBox, Table, Empty } from "../../components/common";

export default function AdminCustomers() {
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("customers");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ legal_name: "", owner_name: "", email: "", phone: "", ein: "", tpt_license: "", entity_type: "LLC", filing_frequency: "" });
  const [created, setCreated] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    const q = new URLSearchParams({ page, page_size: 20, search, status });
    API.get(`/admin/customers?${q}`).then((r) => setData(r.data));
    API.get("/admin/leads").then((r) => setLeads(r.data.leads)).catch(() => {});
  };
  useEffect(() => { load(); }, [page, status]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setErr(""); setCreated(null);
    try {
      const { data: res } = await API.post("/admin/customers", form);
      setCreated(res); toast.success(`Customer ${res.case_number} created`);
      load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-customers-page">
      <PageHeader title="Customer Management" sub="Create, manage, and service every client account."
        right={<button className="btn-navy" onClick={() => { setOpen(true); setErr(""); setCreated(null); }} data-testid="create-customer-btn"><Plus className="h-4 w-4" /> Create Customer</button>} />

      <div className="mb-4 flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-1 w-fit">
        {[["customers", "Customers"], ["leads", `Consultation Leads (${leads.length})`]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
            className={`rounded px-3 py-1.5 text-sm font-semibold ${tab === v ? "bg-navy text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "leads" ? (
        leads.length === 0 ? <Empty title="No consultation leads yet" /> : (
          <Table headers={["Date", "Name", "Business", "Email", "Phone", "Message"]} testid="leads-table">
            {leads.map((l) => (
              <tr key={l.id} className="border-b border-slate-100">
                <td className="px-4 py-2.5 text-slate-500">{fmtDate(l.created_at)}</td>
                <td className="px-4 py-2.5 font-semibold">{l.name}</td>
                <td className="px-4 py-2.5">{l.business_name || "—"}</td>
                <td className="px-4 py-2.5">{l.email}</td>
                <td className="px-4 py-2.5">{l.phone || "—"}</td>
                <td className="px-4 py-2.5 max-w-xs truncate text-slate-500">{l.message}</td>
              </tr>
            ))}
          </Table>
        )
      ) : !data ? <Loading label="Loading customers" /> : (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            <input className="input-gov max-w-xs" placeholder="Search name, email, EIN, TPT, case #…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customer-search" />
            <select className="input-gov max-w-[160px]" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="customer-status-filter">
              <option value="">All statuses</option>
              {["active", "trial", "delinquent", "suspended"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          {data.items.length === 0 ? <Empty title="No customers found" /> : (
            <Table headers={["Case #", "Business", "Contact", "EIN / TPT", "Plan", "Status", "Demo"]} testid="customers-table">
              {data.items.map((b) => (
                <tr key={b.id} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={() => navigate(`/admin/customers/${b.id}`)} data-testid={`customer-row-${b.case_number}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{b.case_number}</td>
                  <td className="px-4 py-2.5"><div className="font-semibold text-navy">{b.legal_name}</div><div className="text-xs text-slate-400">{b.dba}</div></td>
                  <td className="px-4 py-2.5 text-sm"><div>{b.email}</div><div className="text-xs text-slate-400">{b.phone}</div></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500"><div>{b.ein || "—"}</div><div>{b.tpt_license || "—"}</div></td>
                  <td className="px-4 py-2.5 text-sm">{b.plan_name}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={b.status} /></td>
                  <td className="px-4 py-2.5">{b.is_demo && <span className="rounded bg-gold/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">DEMO</span>}</td>
                </tr>
              ))}
            </Table>
          )}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-slate-500">{data.total} customers</span>
            <div className="flex gap-2">
              <button className="btn-outline-gov px-3 py-1.5" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="cust-prev"><ChevronLeft className="h-4 w-4" /></button>
              <button className="btn-outline-gov px-3 py-1.5" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="cust-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Create Customer">
        {created ? (
          <div className="space-y-3 text-center" data-testid="customer-created">
            <div className="text-lg font-bold text-emerald-700">Customer created: {created.case_number}</div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
              Owner login: <strong>{created.owner_email}</strong><br />
              Temporary password: <code className="font-mono font-bold">{created.temporary_password}</code>
              <div className="mt-1 text-xs text-amber-700">Share this securely. It won't be shown again.</div>
            </div>
            <button className="btn-navy w-full" onClick={() => { setOpen(false); navigate(`/admin/customers/${created.business_id}`); }} data-testid="goto-new-customer">Open Customer</button>
          </div>
        ) : (
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-2" data-testid="create-customer-form">
            <Field label="Legal Business Name *"><input className="input-gov" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} required data-testid="cc-legal-name" /></Field>
            <Field label="Owner Name"><input className="input-gov" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="cc-owner" /></Field>
            <Field label="Owner Email *"><input type="email" className="input-gov" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="cc-email" /></Field>
            <Field label="Phone"><input className="input-gov" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cc-phone" /></Field>
            <Field label="EIN *"><input className="input-gov font-mono" placeholder="12-3456789" value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} required data-testid="cc-ein" /></Field>
            <Field label="AZ TPT License"><input className="input-gov" value={form.tpt_license} onChange={(e) => setForm({ ...form, tpt_license: e.target.value })} data-testid="cc-tpt" /></Field>
            <div className="sm:col-span-2"><ErrBox msg={err} /></div>
            <button className="btn-navy sm:col-span-2" disabled={busy} data-testid="cc-submit">{busy ? "Creating…" : "Create Customer"}</button>
          </form>
        )}
      </Modal>
    </div>
  );
}
